import { CheckOutlined, InboxOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { App, Button, Form, Input, InputNumber, Select, Spin } from 'antd';
import Header from 'components/Header/Header';
import { SITE } from 'config/config';
import dayjs from 'dayjs';
import qs from 'query-string';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createPredictions,
  getAllSetting,
  getFP,
  getIP,
  getMatchList,
  getPlayers,
  getTeams,
  getTeamsChampions,
  voteWorldCupChampion,
  voteWorldCupTopScorer,
  voteWorldCupBestPlayer,
  getTime,
  getTopScorerPredictions,
  getBestPlayerPredictions,
  getBestTeamGoalInMatchPredictions,
  voteWorldCupBestTeamGoalInMatch,
  getAsiaTeams,
  getFinalAsiaTeamPredictions,
  voteWorldCupFinalAsiaTeam
} from 'service/base';
import { ChampionPredictionTab, PlayerPredictionTab } from './components/WorldCupPredictionTabs';
import { twMerge } from 'tailwind-merge';
import { CONST_SITE, LocalStorageKey } from 'utils/common/const';

const isClientLoggedIn = () => !!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN);

/** Tên đăng nhập hiển thị ở form dự đoán trận — chỉ khi đã có token */
const getAccountWhenLoggedIn = () => {
  if (!isClientLoggedIn()) return '';
  const saved = localStorage.getItem('userAccount')?.trim();
  if (saved) return saved;
  try {
    const raw = localStorage.getItem(LocalStorageKey.USER_INFO);
    if (!raw) return '';
    const u = JSON.parse(raw);
    return String(u?.username ?? u?.userName ?? u?.account ?? '').trim();
  } catch {
    return '';
  }
};

const resolveMatchFormAccount = (rawData) => {
  const manual = rawData?.account != null && String(rawData.account).trim() !== '';
  if (manual) return String(rawData.account).trim();
  return getAccountWhenLoggedIn();
};

const openAppLoginModal = () => {
  window.dispatchEvent(new CustomEvent('sc88-open-login'));
};

const daysOfWeek = {
  Monday: 'Thứ Hai',
  Tuesday: 'Thứ Ba',
  Wednesday: 'Thứ Tư',
  Thursday: 'Thứ Năm',
  Friday: 'Thứ Sáu',
  Saturday: 'Thứ Bảy',
  Sunday: 'Chủ Nhật'
};

/** Chuẩn hoá payload list từ API (array trực tiếp hoặc { data }, giống pattern OK9/Node) */
function unwrapApiList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.result)) return res.result;
  return [];
}

function mapTeamFromApi(t) {
  const id = String(
    t?.id ?? t?.teamId ?? t?._id ?? t?.code ?? t?.isoCode ?? t?.countryCode ?? ''
  ).trim();
  const name = t?.name_vi ?? t?.name ?? t?.teamName ?? t?.title ?? t?.label ?? '';
  let flag = t?.flag ?? t?.logo ?? t?.imageUrl ?? t?.image ?? t?.avatar ?? t?.photo ?? '';
  if (!flag && id && /^[a-z]{2}$/i.test(id)) {
    flag = `https://flagcdn.com/w80/${id.toLowerCase()}.png`;
  }
  if (!id || !name) return null;
  return { id, name, flag: flag || undefined };
}

/** GET /players — payload phần tử: { _id, name, avatar, teamName } + { code, data } ở root */
function mapPlayerFromApi(p) {
  const id = String(p?.id ?? p?._id ?? p?.playerId ?? '').trim();
  const name = p?.name ?? p?.playerName ?? p?.fullName ?? '';
  const photo = p?.avatar ?? p?.photo ?? p?.image ?? p?.imageUrl ?? '';
  const teamName = p?.teamName ?? p?.team_name ?? '';
  if (!id || !name) return null;
  return {
    id,
    name,
    photo: photo || undefined,
    teamName: teamName || undefined,
    teamId: p?.teamId ?? p?.team_id,
    countryCode: p?.countryCode ?? p?.country_code
  };
}

/**
 * Một dòng từ GET /worldcup/teams-champions
 * Payload: { code, name, name_vi, flag, count, percent }
 * @see https://api-ddts-sc88.attcloud.org/api/worldcup/teams-champions
 */
/** GET /worldcup/top-scorer-predictions | /worldcup/best-player-predictions — cùng shape: { id, name, teamName, avatar, count, percent? } */
function mapTopScorerPredictionRow(r, index) {
  const id = String(r?.id ?? r?._id ?? r?.playerId ?? '').trim();
  const name = r?.name ?? r?.playerName ?? '';
  const photo = r?.avatar ?? r?.photo ?? r?.image ?? '';
  const countRaw = r?.count ?? r?.votes ?? r?.voteCount ?? 0;
  const votes = typeof countRaw === 'number' ? countRaw : Number(String(countRaw).replace(/\D/g, '')) || 0;
  if (!id || !name) return null;
  return {
    id,
    name,
    votes,
    photo: photo || undefined,
    percent: typeof r?.percent === 'number' ? r.percent : undefined,
    teamName: r?.teamName ?? r?.team_name
  };
}

function mapTeamsChampionsRow(r, index) {
  const code = String(r?.code ?? '').trim();
  const name =
    r?.name_vi ||
    r?.name ||
    r?.teamName ||
    r?.title ||
    r?.label ||
    '';
  const votesRaw = r?.count ?? r?.votes ?? r?.voteCount ?? r?.score ?? 0;
  const votes = typeof votesRaw === 'number' ? votesRaw : Number(String(votesRaw).replace(/\D/g, '')) || 0;
  let flag = r?.flag ?? r?.logo ?? r?.imageUrl ?? r?.image ?? '';
  const idGuess = code || (r?.id ?? r?.teamId ?? r?.countryCode);
  const idStr = String(idGuess ?? '').trim();
  if (!flag && idStr && /^[a-z]{2}$/i.test(idStr)) {
    flag = `https://flagcdn.com/w80/${idStr.toLowerCase()}.png`;
  }
  if (!name) return null;
  const safeId = code || idStr || `champion-row-${index}`;
  return { id: safeId, name, votes, flag: flag || undefined, percent: r?.percent };
}


const leagues = [
  {
    id: 8,
    name: 'FIFA World Cup',
    logo: 'https://att-bucket.sgp1.cdn.digitaloceanspaces.com/logo/wc-2026.png'
  },
  {
    id: 1,
    name: 'UEFA Champions League',
    logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/f/f5/UEFA_Champions_League.svg/960px-UEFA_Champions_League.svg.png'
  },
  {
    id: 2,
    name: 'UEFA Europa League',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/1/1b/UEFA_Europa_League_logo_%282024_version%29.svg'
  },
  {
    id: 3,
    name: 'English Premier League',
    logo: 'https://loodibee.com/wp-content/uploads/Premier-League-Logo.png'
  },
  {
    id: 4,
    name: 'La Liga',
    logo: 'https://api-ddts-on68.attcloud.org/public/uploads/file-1776497700383-85707437.png'
  },
  {
    id: 5,
    name: 'Italian Serie A',
    logo: 'https://1000logos.net/wp-content/uploads/2019/01/Italian-Serie-A-Logo.png'
  },
  {
    id: 6,
    name: 'German Bundesliga',
    logo: 'https://api-ddts-on68.attcloud.org/public/uploads/file-1776497741004-506738821.png'
  },
  {
    id: 7,
    name: 'France Ligue 1',
    logo: 'https://api-ddts-on68.attcloud.org/public/uploads/file-1776497881490-491170157.png'
  }
];

const HomePage = () => {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [listDataMatch, setListDataMatch] = useState([]);
  const [timeServer, setTimeServer] = useState(null);
  const [IP, setIP] = useState(null);
  const [FP, setFP] = useState(null);
  const [dataSettings, setDataSettings] = useState([]);
  const [isPredictConfirmOpen, setIsPredictConfirmOpen] = useState(false);
  const [pendingMatchPredictions, setPendingMatchPredictions] = useState([]);
  const showMatchPredictionTab = true;

  const predictionTabs = [
    {
      key: 'match',
      label: 'Dự đoán trận đấu',
      icon: '/assets/images/tab/1.png',
      iconActive: '/assets/images/tab/1-1.png'
    },
    {
      key: 'champion',
      label: 'Đội vô địch',
      icon: '/assets/images/tab/2.png',
      iconActive: '/assets/images/tab/2-1.png'
    },
    {
      key: 'top-scorer',
      label: 'Vua phá lưới',
      icon: '/assets/images/tab/3.png',
      iconActive: '/assets/images/tab/3-1.png'
    },
    {
      key: 'best-player',
      label: 'Cầu thủ xuất sắc nhất',
      icon: '/assets/images/tab/4.png',
      iconActive: '/assets/images/tab/4-1.png'
    },
    {
      key: 'final-asia-team',
      label: 'Đội Châu Á vào sâu nhất',
      icon: '/assets/images/tab/5.png',
      iconActive: '/assets/images/tab/5-1.png'
    },
    {
      key: 'best_team_goal_in_match',
      label: 'Đội ghi nhiều bàn nhất trong 1 trận',
      icon: '/assets/images/tab/6.png',
      iconActive: '/assets/images/tab/6-1.png'
    }
  ];
  const [activePredictionTab, setActivePredictionTab] = useState('match');
  const [championQuery, setChampionQuery] = useState('');
  const [isChampionAutoFilled, setIsChampionAutoFilled] = useState(false);
  const [selectedChampionId, setSelectedChampionId] = useState(null);
  const [isChampionVoting, setIsChampionVoting] = useState(false);
  const [topScorerQuery, setTopScorerQuery] = useState('');
  const [isTopScorerAutoFilled, setIsTopScorerAutoFilled] = useState(false);
  const [selectedTopScorerId, setSelectedTopScorerId] = useState(null);
  const [isTopScorerVoting, setIsTopScorerVoting] = useState(false);
  const [bestPlayerQuery, setBestPlayerQuery] = useState('');
  const [isBestPlayerAutoFilled, setIsBestPlayerAutoFilled] = useState(false);
  const [selectedBestPlayerId, setSelectedBestPlayerId] = useState(null);
  const [isBestPlayerVoting, setIsBestPlayerVoting] = useState(false);
  const [bestTeamGoalQuery, setBestTeamGoalQuery] = useState('');
  const [isBestTeamGoalAutoFilled, setIsBestTeamGoalAutoFilled] = useState(false);
  const [selectedBestTeamGoalId, setSelectedBestTeamGoalId] = useState(null);
  const [isBestTeamGoalVoting, setIsBestTeamGoalVoting] = useState(false);
  const [finalAsiaQuery, setFinalAsiaQuery] = useState('');
  const [isFinalAsiaAutoFilled, setIsFinalAsiaAutoFilled] = useState(false);
  const [selectedFinalAsiaTeamId, setSelectedFinalAsiaTeamId] = useState(null);
  const [isFinalAsiaVoting, setIsFinalAsiaVoting] = useState(false);
  const [voteConfirm, setVoteConfirm] = useState({
    open: false,
    type: '',
    title: '',
    selectedName: '',
    selectedImage: '',
    payload: null,
    error: ''
  });

  const [championTeams, setChampionTeams] = useState([]);
  /** Cầu thủ WC từ GET /players — dùng cho tab Vua phá lưới sau này */
  const [wcPlayers, setWcPlayers] = useState([]);

  const [championLeaderboard, setChampionLeaderboard] = useState([]);
  const [topScorerLeaderboard, setTopScorerLeaderboard] = useState([]);
  const [bestPlayerLeaderboard, setBestPlayerLeaderboard] = useState([]);
  const [bestTeamGoalLeaderboard, setBestTeamGoalLeaderboard] = useState([]);
  const [finalAsiaTeams, setFinalAsiaTeams] = useState([]);
  const [finalAsiaLeaderboard, setFinalAsiaLeaderboard] = useState([]);

  const topScorerPlayersList = useMemo(() => {
    return wcPlayers.map((p) => ({
      id: String(p.id),
      name: p.name,
      teamName: p.teamName,
      photo: p.photo
    }));
  }, [wcPlayers]);

  const bestPlayerPlayersList = useMemo(() => {
    return wcPlayers.map((p) => ({
      id: String(p.id),
      name: p.name,
      teamName: p.teamName,
      photo: p.photo
    }));
  }, [wcPlayers]);

  const [selectedLeagues, setSelectedLeagues] = useState([]);
  /** Giải đang hiển thị thẻ dự đoán nền frame-match (click logo giải) */
  const [leagueFrameDetailId, setLeagueFrameDetailId] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'today', 'tomorrow', 'day3', 'day4', 'day5'
  const getMatchSourceList = () => {
    return (listDataMatch || []).map((item) => ({
      ...item,
      countdown: item.countdown || new Date(dayjs.unix(item.startTime))
    }));
  };

  // Hàm tạo danh sách options cho Select
  const getDateOptions = () => {
    const now = dayjs();
    const options = [
      { value: 'all', label: 'Thời gian: Tất cả' },
      { value: 'today', label: `Hôm nay: ${now.format('DD/MM/YYYY')}` },
      { value: 'tomorrow', label: `Ngày mai: ${now.add(1, 'day').format('DD/MM/YYYY')}` },
      {
        value: 'day3',
        label: `${daysOfWeek[now.add(2, 'day').format('dddd')]}: ${now
          .add(2, 'day')
          .format('DD/MM/YYYY')}`
      },
      {
        value: 'day4',
        label: `${daysOfWeek[now.add(3, 'day').format('dddd')]}: ${now
          .add(3, 'day')
          .format('DD/MM/YYYY')}`
      },
      {
        value: 'day5',
        label: `${daysOfWeek[now.add(4, 'day').format('dddd')]}: ${now
          .add(4, 'day')
          .format('DD/MM/YYYY')}`
      }
    ];
    return options;
  };

  // Hàm lọc trận đấu theo tab được chọn
  const getFilteredMatches = () => {
    const sourceMatchList = getMatchSourceList();
    if (!sourceMatchList || sourceMatchList.length === 0) return [];

    const now = dayjs();
    const today = now.format('YYYY-MM-DD');
    const tomorrow = now.add(1, 'day').format('YYYY-MM-DD');
    const day3 = now.add(2, 'day').format('YYYY-MM-DD');
    const day4 = now.add(3, 'day').format('YYYY-MM-DD');
    const day5 = now.add(4, 'day').format('YYYY-MM-DD');

    switch (activeTab) {
      case 'today':
        return sourceMatchList.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === today;
        });
      case 'tomorrow':
        return sourceMatchList.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === tomorrow;
        });
      case 'day3':
        return sourceMatchList.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === day3;
        });
      case 'day4':
        return sourceMatchList.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === day4;
        });
      case 'day5':
        return sourceMatchList.filter((match) => {
          const matchDate = dayjs.unix(match.startTime).format('YYYY-MM-DD');
          return matchDate === day5;
        });
      case 'all':
      default:
        return sourceMatchList;
    }
  };

  const handleLeagueClick = (leagueId) => {
    setSelectedLeagues((prevSelectedLeagues) => {
      const isRemoving = prevSelectedLeagues.includes(leagueId);
      const next = isRemoving
        ? prevSelectedLeagues.filter((id) => id !== leagueId)
        : [...prevSelectedLeagues, leagueId];
      if (!isRemoving) {
        setLeagueFrameDetailId(leagueId);
      } else {
        setLeagueFrameDetailId((cur) =>
          cur === leagueId ? (next.length ? next[next.length - 1] : null) : cur
        );
      }
      return next;
    });
  };

  /** Trận hiển thị trong khung frame-match: chỉ dùng dữ liệu API */
  const buildLeagueFrameMatch = (leagueId) => {
    const league = leagues.find((l) => l.id === leagueId);
    if (!league) return null;

    const source = getMatchSourceList();
    const forLeague = source.filter((m) => m.leagueName === league.name);
    if (forLeague.length > 0) {
      const item = forLeague[0];
      const endTs = item.endTime ?? item.startTime + 7200;
      return { ...item, endTime: endTs, leagueFrameLogo: league.logo };
    }

    return null;
  };

  /** Gửi form: gồm cả trận đang hiển thị ở khung frame-match (fake) nếu chưa có trong danh sách lọc */
  const getMatchesForPredictionSubmit = () => {
    const base = getFilteredMatches();
    if (leagueFrameDetailId == null) return base;
    const frame = buildLeagueFrameMatch(leagueFrameDetailId);
    if (!frame) return base;
    const exists = base.some((m) => String(m.matchId) === String(frame.matchId));
    if (exists) return base;
    return [...base, frame];
  };

  const [scrollPosition, setScrollPosition] = useState(0);
  const leagueListRef = React.createRef();

  const scrollLeft = () => {
    if (leagueListRef.current) {
      leagueListRef.current.scrollBy({ left: -100, behavior: 'smooth' });
      setScrollPosition(leagueListRef.current.scrollLeft - 100);
    }
  };

  const scrollRight = () => {
    if (leagueListRef.current) {
      leagueListRef.current.scrollBy({ left: 100, behavior: 'smooth' });
      setScrollPosition(leagueListRef.current.scrollLeft + 100);
    }
  };

  useEffect(() => {
    fetchAllSetting();
    fetchIP();
    fetchFP();
    fetchTimeServer();
  }, []);

  const syncMatchFormAccountFromAuth = useCallback(() => {
    const acc = getAccountWhenLoggedIn();
    if (acc) {
      form.setFieldsValue({ account: acc });
    }
  }, [form]);

  useEffect(() => {
    syncMatchFormAccountFromAuth();
  }, [syncMatchFormAccountFromAuth]);

  useEffect(() => {
    const onAuth = () => syncMatchFormAccountFromAuth();
    window.addEventListener('sc88-auth-change', onAuth);
    return () => window.removeEventListener('sc88-auth-change', onAuth);
  }, [syncMatchFormAccountFromAuth]);

  useEffect(() => {
    fetchDataScheduByLS();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const apiParams = { params: { site: CONST_SITE.SC88 } };
    (async () => {
      try {
        const [
          teamsRes,
          playersRes,
          championsRes,
          bestTeamGoalRes,
          topScorerPredRes,
          bestPlayerPredRes,
          asiaTeamsRes,
          finalAsiaPredRes
        ] = await Promise.all([
          getTeams(apiParams),
          getPlayers(apiParams),
          getTeamsChampions(),
          getBestTeamGoalInMatchPredictions(apiParams),
          getTopScorerPredictions(apiParams),
          getBestPlayerPredictions(apiParams),
          getAsiaTeams(apiParams),
          getFinalAsiaTeamPredictions(apiParams)
        ]);
        if (cancelled) return;
        const teams = unwrapApiList(teamsRes).map(mapTeamFromApi).filter(Boolean);
        setChampionTeams(teams);
        const rawPlayers =
          playersRes?.code === 0 && Array.isArray(playersRes?.data)
            ? playersRes.data
            : unwrapApiList(playersRes);
        const players = rawPlayers.map(mapPlayerFromApi).filter(Boolean);
        setWcPlayers(players);
        const rawChampions =
          championsRes?.code === 0 && Array.isArray(championsRes?.data)
            ? championsRes.data
            : unwrapApiList(championsRes);
        const championRows = rawChampions
          .map((row, i) => mapTeamsChampionsRow(row, i))
          .filter(Boolean)
          .sort((a, b) => (b.votes || 0) - (a.votes || 0));
        setChampionLeaderboard(championRows);
        const rawBestTeamGoal =
          bestTeamGoalRes?.code === 0 && Array.isArray(bestTeamGoalRes?.data)
            ? bestTeamGoalRes.data
            : unwrapApiList(bestTeamGoalRes);
        const bestTeamGoalRows = rawBestTeamGoal
          .map((row, i) => mapTeamsChampionsRow(row, i))
          .filter(Boolean)
          .sort((a, b) => (b.votes || 0) - (a.votes || 0));
        setBestTeamGoalLeaderboard(bestTeamGoalRows);
        const rawTopScorer =
          topScorerPredRes?.code === 0 && Array.isArray(topScorerPredRes?.data)
            ? topScorerPredRes.data
            : unwrapApiList(topScorerPredRes);
        const topScorerRows = rawTopScorer
          .map((row, i) => {
            const m = mapTopScorerPredictionRow(row, i);
            return m ? { m, i } : null;
          })
          .filter(Boolean)
          .sort((a, b) => {
            const dv = (b.m.votes || 0) - (a.m.votes || 0);
            if (dv !== 0) return dv;
            return a.i - b.i;
          })
          .slice(0, 10)
          .map((x) => x.m);
        setTopScorerLeaderboard(topScorerRows);
        const rawBestPlayer =
          bestPlayerPredRes?.code === 0 && Array.isArray(bestPlayerPredRes?.data)
            ? bestPlayerPredRes.data
            : unwrapApiList(bestPlayerPredRes);
        const bestPlayerRows = rawBestPlayer
          .map((row, i) => {
            const m = mapTopScorerPredictionRow(row, i);
            return m ? { m, i } : null;
          })
          .filter(Boolean)
          .sort((a, b) => {
            const dv = (b.m.votes || 0) - (a.m.votes || 0);
            if (dv !== 0) return dv;
            return a.i - b.i;
          })
          .slice(0, 10)
          .map((x) => x.m);
        setBestPlayerLeaderboard(bestPlayerRows);

        const rawAsiaTeams =
          asiaTeamsRes?.code === 0 && Array.isArray(asiaTeamsRes?.data)
            ? asiaTeamsRes.data
            : unwrapApiList(asiaTeamsRes);
        const asiaTeamsMapped = rawAsiaTeams.map(mapTeamFromApi).filter(Boolean);
        setFinalAsiaTeams(asiaTeamsMapped);

        const rawFinalAsia =
          finalAsiaPredRes?.code === 0 && Array.isArray(finalAsiaPredRes?.data)
            ? finalAsiaPredRes.data
            : unwrapApiList(finalAsiaPredRes);
        const finalAsiaRows = rawFinalAsia
          .map((row, i) => mapTeamsChampionsRow(row, i))
          .filter(Boolean)
          .sort((a, b) => (b.votes || 0) - (a.votes || 0));
        setFinalAsiaLeaderboard(finalAsiaRows);
      } catch (e) {
        console.error(
          'Teams/players/champions/best-team-goal/top-scorer/best-player/final-asia API:',
          e
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchIP = async () => {
    try {
      const res = await getIP();
      setIP(res);
    } catch (e) {}
  };

  const fetchFP = async () => {
    try {
      const res = await getFP();
      setFP(res);
    } catch (e) {}
  };

  const fetchTimeServer = async () => {
    try {
      const res = await getTime();
      setTimeServer(res);
    } catch (e) {}
  };

  const fetchDataScheduByLS = async () => {
    try {
      setIsLoading(true);
      const res2 = await getMatchList({
        site: CONST_SITE.SC88
      });

      const dataMatch2 =
        res2?.data?.length > 0
          ? res2.data.map((item) => {
              const formatCountDown = dayjs.unix(item.startTime);
              return { ...item, countdown: new Date(formatCountDown) };
            })
          : [];

      setListDataMatch(dataMatch2 || []);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching match list:', error);
      setIsLoading(false);
    }
  };

  const fetchAllSetting = async (data) => {
    try {
      setIsLoading(true);
      const res = await getAllSetting(qs.stringify({ site: CONST_SITE.SC88 }));
      setDataSettings(res?.data || []);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const TIMEOUT_DURATION = 10000; // 10 seconds

  const createPredictionsWithTimeout = (predictions) => {
    return Promise.race([
      createPredictions(predictions),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out')), TIMEOUT_DURATION)
      )
    ]);
  };

  const onConfirmMatchPrediction = async () => {
    if (pendingMatchPredictions.length === 0) {
      setIsPredictConfirmOpen(false);
      return;
    }

    try {
      setIsLoadingSubmit(true);

      const res = await createPredictionsWithTimeout(pendingMatchPredictions);
      if (res?.code === 0) {
        const account = pendingMatchPredictions[0]?.account;
        if (account) {
          localStorage.setItem('userAccount', account);
        }

        notification.success({
          message: 'Thành công',
          description: 'Dự đoán tỉ số thành công!'
        });
        form.resetFields(Object.keys(form.getFieldsValue()).filter((key) => key !== 'account'));
        setIsPredictConfirmOpen(false);
        setPendingMatchPredictions([]);
        fetchDataScheduByLS();
      } else {
        if (res.valid === false) {
          notification.error({
            message: res.title_mess,
            description: res?.text_mess
          });
        } else {
          notification.error({
            message: 'Lỗi',
            description: res?.message
          });
        }
      }
      setIsLoadingSubmit(false);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Đã xảy ra lỗi khi gửi dự đoán!'
      });
      setIsLoadingSubmit(false);
    }
  };

  const onCancelMatchConfirm = () => {
    if (isLoadingSubmit) return;
    setIsPredictConfirmOpen(false);
  };

  const submitMatchPredictions = async (rawData) => {
    try {
      if (!isClientLoggedIn()) {
        openAppLoginModal();
        return;
      }

      const account = resolveMatchFormAccount(rawData);
      const data = { ...rawData, account };

      const filteredMatches = getMatchesForPredictionSubmit();
      if (filteredMatches?.length === 0) return;

      if (!data?.account) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng nhập tên đăng nhập hoặc đăng nhập để dùng tài khoản của bạn!'
        });
        return;
      }

      // Validate that both home and away scores are provided for each match
      const hasInvalidScores = Object.entries(data).some(([key, value]) => {
        // Bỏ qua field account và các field không phải score
        if (key === 'account' || !key.includes('_')) return false;

        const parts = key.split('_');
        if (parts.length !== 2) return false;

        const [scoreType, matchId] = parts;
        if (!['homeScore', 'awayScore'].includes(scoreType)) return false;

        const otherScoreType = scoreType === 'homeScore' ? 'awayScore' : 'homeScore';
        const otherScoreKey = `${otherScoreType}_${matchId}`;
        const otherValue = data[otherScoreKey];

        // Kiểm tra nếu một trong hai score có giá trị nhưng score kia không có
        const hasValue = value !== undefined && value !== null && value !== '';
        const hasOtherValue = otherValue !== undefined && otherValue !== null && otherValue !== '';

        if (hasValue && !hasOtherValue) {
          return true;
        }

        return false;
      });

      if (hasInvalidScores) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng nhập đầy đủ cả hai bên tỉ số cho mỗi trận đấu!'
        });
        return;
      }

      const resultGroup = groupScoresByMatchId(data, IP, FP, filteredMatches);

      if (resultGroup.length === 0) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng dự đoán ít nhất một trận đấu!'
        });
        return;
      }

      setPendingMatchPredictions(resultGroup);
      setIsPredictConfirmOpen(true);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Đã xảy ra lỗi khi gửi dự đoán!'
      });
    }
  };

  const onFinishForm = async (data) => {
    await submitMatchPredictions(data);
  };

  /** Nút XÁC NHẬN trên thẻ trận nổi bật: cùng luồng form (toàn bộ trận đã điền), account từ token nếu đã đăng nhập */
  const handleMatchCardConfirmClick = () => {
    submitMatchPredictions(form.getFieldsValue(true));
  };

  const onFinishFailed = (errorInfo) => {
    // Chưa đăng nhập: validate form chặn onFinish → vẫn mở popup đăng nhập
    if (!isClientLoggedIn()) {
      openAppLoginModal();
      return;
    }
    if (!errorInfo?.values?.account) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng nhập tên đang nhập!'
      });
    } else {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng điền đầy đủ thông tin!'
      });
    }
  };

  const onFinishCountDown = () => {
    console.log('Countdown finished');
  };

  const groupScoresByMatchId = (input, ip, fp, matchInfo) => {
    const groupedMatches = Object.entries(input).reduce((acc, [key, value]) => {
      if (key === 'account') return acc;

      const parts = key.split('_');
      if (parts.length !== 2) return acc;

      const [scoreType, matchId] = parts;
      if (!matchId || !['homeScore', 'awayScore', 'sameScore'].includes(scoreType)) return acc;

      // Kiểm tra giá trị hợp lệ
      if (value === undefined || value === null || value === '') return acc;

      if (!acc[matchId]) {
        acc[matchId] = {
          matchId,
          account: input.account,
          homeScore: undefined,
          awayScore: undefined,
          sameScore: undefined
        };
      }

      if (scoreType === 'homeScore') {
        acc[matchId].homeScore = value;
      } else if (scoreType === 'awayScore') {
        acc[matchId].awayScore = value;
      } else if (scoreType === 'sameScore') {
        acc[matchId].sameScore = value;
      }

      return acc;
    }, {});

    return Object.values(groupedMatches)
      .filter(
        (match) =>
          match.homeScore !== undefined &&
          match.awayScore !== undefined &&
          match.sameScore !== undefined &&
          match.homeScore !== null &&
          match.awayScore !== null &&
          match.sameScore !== null &&
          match.homeScore !== '' &&
          match.sameScore !== '' &&
          match.awayScore !== ''
      )
      .map(({ matchId, account, homeScore, awayScore, sameScore }) => {
        const matchDetails = matchInfo.find(
          (match) => parseInt(match.matchId) === parseInt(matchId)
        );

        return {
          matchId,
          homeScore,
          awayScore,
          sameScore,
          account,
          ip,
          fp,
          homeName: matchDetails ? matchDetails.homeName : 'Unknown',
          awayName: matchDetails ? matchDetails.awayName : 'Unknown',
          site: SITE
        };
      });
  };

  const renderFeaturedMatches = (matches = []) => {
    if (!matches?.length) return null;

    return (
      <div className="w-full mt-4 lg:mt-6">
        <div className="w-full h-px bg-[#d7d7d7]" />
        <div className="flex items-center justify-center mt-4 lg:mt-5 px-2">
          <div
            className="relative flex w-full max-w-[min(100%,400px)]  items-center justify-center bg-no-repeat px-10 py-2 lg:max-h-[30px] lg:px-14"
            style={{
              backgroundImage: 'url(/assets/images/match/frametitle.png)',
              backgroundSize: '100% 100%',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            <span className="font-black uppercase text-white text-[13px] sm:text-[14px] lg:text-[22px] leading-none drop-shadow-sm">
              TRẬN ĐẤU NỔI BẬT
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mt-4">
          {matches.slice(0, 2).map((item) => (
            <div
              key={`featured-${item.matchId}`}
              className="bg-[#edeff1] border border-[#dcdfe3] rounded-[20px] flex flex-col gap-3 relative"
            >
              <div className="self-start bg-[#f27121] w-fit max-w-[95%] rounded-tl-[14px] rounded-br-[14px] px-3 py-1">
                <p className="font-bold text-[15px] leading-5 text-white mb-0 whitespace-nowrap">
                  {dayjs(item.startTime * 1000).format('HH:mm DD/MM')}
                </p>
              </div>

              <div className="flex gap-3 items-center px-4 lg:px-5">
                <div className="flex-1 flex flex-col gap-3">
                  <div className="flex gap-3 items-center">
                    <div className="w-8 h-8 shrink-0 flex items-center justify-center">
                      <img alt={item.homeName} className="w-full h-full object-contain" src={item.homeLogo} />
                    </div>
                    <p className="font-bold text-base leading-6 text-[#f27121] mb-0 truncate">
                      {item.homeName}
                    </p>
                  </div>
                  <div className="flex gap-3 items-center">
                    <div className="w-8 h-8 shrink-0 flex items-center justify-center">
                      <img alt={item.awayName} className="w-full h-full object-contain" src={item.awayLogo} />
                    </div>
                    <p className="font-bold text-base leading-6 text-[#f27121] mb-0 truncate">
                      {item.awayName}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <div className="bg-[#dcdfe3] border-0 text-center font-bold text-sm text-[#3e4c5c] rounded-[8px] w-[36px] h-[36px] flex items-center justify-center">
                    -
                  </div>
                  <div className="bg-[#dcdfe3] border-0 text-center font-bold text-sm text-[#3e4c5c] rounded-[8px] w-[36px] h-[36px] flex items-center justify-center">
                    -
                  </div>
                </div>
              </div>

              <div className="flex gap-2 lg:gap-3 items-center px-4 lg:px-5">
                <div className="flex-1 bg-white border border-[#b8bfc7] rounded-[8px] px-3 h-[36px] lg:h-[40px] flex items-center text-[12px] lg:text-base text-[#6b7280]">
                  Số người dự đoán giống bạn
                </div>
                <Button
                  type="primary"
                  htmlType="button"
                  onClick={handleMatchCardConfirmClick}
                  className="bg-[#f27121] border border-[#fac6a6] h-[36px] lg:h-[40px] text-white font-bold text-[12px] lg:text-sm rounded-[8px] px-3 lg:px-4 whitespace-nowrap"
                >
                  XÁC NHẬN
                </Button>
              </div>

              <div className="bg-[#f27121] rounded-bl-[20px] rounded-br-[20px] px-4 py-1">
                <p className="font-bold text-base leading-6 text-white text-center mb-0 whitespace-nowrap truncate">
                  {item.leagueName}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLeagueFrameDetail = () => {
    if (leagueFrameDetailId == null) return null;
    const item = buildLeagueFrameMatch(leagueFrameDetailId);
    if (!item) return null;

    const disabled = timeServer != null && item.countdown != null && timeServer > item.countdown;
    const startD = dayjs.unix(item.startTime);
    const endD = dayjs.unix(item.endTime ?? item.startTime + 7200);

    return (
      <div className="w-full px-2 sm:px-3 mt-4">
        <div
          className="relative w-full overflow-hidden rounded-2xl border border-[#fac6a6]/70 shadow-[0_8px_28px_rgba(242,113,33,0.14)] px-3 py-4 sm:px-5 sm:py-5 min-[1024px]:px-7 min-[1024px]:py-6"
          style={{
            backgroundImage: 'url(/assets/images/match/frame-match.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4 sm:mb-5">
            <div className="flex flex-col gap-2 text-[11px] sm:text-xs text-[#4b5563]">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-[#374151]">Thời gian bắt đầu:</span>
                <span className="rounded-full bg-[#f27121] px-2 py-0.5 text-[10px] font-bold text-white whitespace-nowrap">
                  {startD.format('HH:mm DD/MM/YYYY')}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-[#374151]">Thời gian kết thúc:</span>
                <span className="rounded-full bg-[#f27121]/90 px-2 py-0.5 text-[10px] font-bold text-white whitespace-nowrap">
                  {endD.format('HH:mm DD/MM/YYYY')}
                </span>
              </div>
            </div>
            <div className="self-stretch sm:self-auto shrink-0 rounded-[10px] bg-[#f27121] px-3 py-1.5 sm:px-4 sm:py-2 flex items-center justify-center">
              <p className="mb-0 text-center font-black text-white text-[13px] sm:text-[15px] leading-tight whitespace-nowrap">
                {startD.format('HH:mm DD/MM/YYYY')}
              </p>
            </div>
          </div>

          <div className="relative mx-auto flex max-w-[720px] items-center justify-between gap-1.5 sm:gap-4 px-0 sm:px-1">
            <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5 sm:gap-2">
              <div className="flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-white shadow-md">
                <img
                  src={item.homeLogo}
                  alt={item.homeName}
                  className="h-full w-full object-contain p-1"
                />
              </div>
              <p className="mb-0 max-w-full truncate text-center font-bold text-[11px] sm:text-sm text-[#f27121] px-0.5">
                {item.homeName}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-1 sm:gap-2">
              <Form.Item className="mb-0" name={`homeScore_${item.matchId}`}>
                <InputNumber
                  disabled={disabled}
                  controls={false}
                  min={0}
                  max={99}
                  maxLength={2}
                  placeholder="-"
                  className="bg-white/95 border border-[#e5e7eb] w-full text-center font-bold text-sm text-[#3e4c5c] rounded-lg shadow-sm"
                  style={{
                    textAlign: 'center',
                    width: '48px',
                    height: '40px',
                    padding: '6px 8px'
                  }}
                />
              </Form.Item>
              <span className="text-lg sm:text-2xl font-black text-[#f27121] leading-none">:</span>
              <Form.Item className="mb-0" name={`awayScore_${item.matchId}`}>
                <InputNumber
                  disabled={disabled}
                  controls={false}
                  min={0}
                  max={99}
                  maxLength={2}
                  placeholder="-"
                  className="bg-white/95 border border-[#e5e7eb] w-full text-center font-bold text-sm text-[#3e4c5c] rounded-lg shadow-sm"
                  style={{
                    textAlign: 'center',
                    width: '48px',
                    height: '40px',
                    padding: '6px 8px'
                  }}
                />
              </Form.Item>
            </div>

            <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5 sm:gap-2">
              <div className="flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-white shadow-md">
                <img
                  src={item.awayLogo}
                  alt={item.awayName}
                  className="h-full w-full object-contain p-1"
                />
              </div>
              <p className="mb-0 max-w-full truncate text-center font-bold text-[11px] sm:text-sm text-[#f27121] px-0.5">
                {item.awayName}
              </p>
            </div>
          </div>

          <div className="mx-auto mt-4 max-w-[560px] space-y-3">
            <div className="w-full rounded-lg border border-[#b8bfc7] bg-white/95 px-2 h-[40px] flex items-center shadow-sm">
              <Form.Item className="mb-0 w-full" name={`sameScore_${item.matchId}`}>
                <InputNumber
                  disabled={disabled}
                  controls={false}
                  min={0}
                  maxLength={5}
                  placeholder="Số người dự đoán giống bạn"
                  className="w-full !border-0 !shadow-none !h-[40px] text-center font-normal text-sm text-[#3e4c5c] [&_.ant-input-number-input]:!h-full [&_.ant-input-number-input]:!leading-[40px] [&_.ant-input-number-input]:!p-0"
                />
              </Form.Item>
            </div>
            <div className="flex justify-center">
              <Button
                type="primary"
                htmlType="button"
                loading={isLoadingSubmit}
                onClick={handleMatchCardConfirmClick}
                className="bg-[#f27121] border border-[#fac6a6] h-[40px] min-w-[160px] text-white font-bold text-sm rounded-lg px-8"
                style={{
                  background: 'linear-gradient(to right, #f27121, #f27121)',
                  border: '1px solid #fac6a6'
                }}
              >
                XÁC NHẬN
              </Button>
            </div>
          </div>

          <div className="pointer-events-none mt-3 flex justify-end items-center gap-2 pr-0 sm:absolute sm:bottom-4 sm:right-5 sm:mt-0">
            {item.leagueFrameLogo ? (
              <img src={item.leagueFrameLogo} alt="" className="h-7 sm:h-9 object-contain max-w-[120px]" />
            ) : null}
            <span className="text-[10px] sm:text-[11px] font-bold uppercase text-[#5c6570] max-w-[min(140px,40vw)] truncate text-right">
              {item.leagueName}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const showListItemMatch = (matchList) => {
    if (!matchList || matchList.length <= 0) {
      const featuredMatches = getMatchSourceList().slice(0, 2);
      return (
        <div className="w-full py-4 px-2 lg:px-3">
          <p className="text-center text-[#4b5563] text-[15px] lg:text-[22px] font-medium mt-2">
            Hiện tại không có trận đấu nào để dự đoán
          </p>
          {renderFeaturedMatches(featuredMatches)}
        </div>
      );
    } else {
      const filteredMatchList =
        selectedLeagues.length > 0
          ? matchList.filter((item) =>
              selectedLeagues.includes(
                leagues.find((league) => league.name === item.leagueName)?.id
              )
            )
          : matchList;

      const filteredByTime = filteredMatchList.filter((item) => timeServer <= item.countdown);

      const itemMatch = filteredByTime.map((item, index) => {
        // const matchDayOfWeek = daysOfWeek[dayjs(item.startTime * 1000).format('dddd')]; // Calculate day of week for each match
        return (
          <div
            key={index}
            className="bg-[#edeff1] border border-[#dcdfe3] rounded-[20px] flex flex-col gap-3 relative"
          >
            {/* Time/Date Badge - Top Left */}
            <div className="self-start bg-[#f27121] w-fit max-w-[95%] rounded-tl-[20px] rounded-br-[20px] px-3 py-1">
              <p className="font-bold text-[15px] leading-5 text-white mb-0 whitespace-nowrap">
                {dayjs(item.startTime * 1000).format('HH:mm DD/MM')}
              </p>
            </div>

            {/* Teams and Score Inputs */}
            <div className="flex gap-3 items-center px-5">
              {/* Teams Column */}
              <div className="flex-1 flex flex-col gap-3">
                {/* Home Team */}
                <div className="flex gap-3 items-center">
                  <div className="w-8 h-8 shrink-0 flex items-center justify-center">
                    <img
                      alt={item.homeName}
                      className="w-full h-full object-contain"
                      src={item.homeLogo}
                    />
                  </div>
                  <p className="font-bold text-base leading-6 text-[#f27121] mb-0 whitespace-nowrap">
                    {item.homeName}
                  </p>
                </div>

                {/* Away Team */}
                <div className="flex gap-3 items-center">
                  <div className="w-8 h-8 shrink-0 flex items-center justify-center">
                    <img
                      alt={item.awayName}
                      className="w-full h-full object-contain"
                      src={item.awayLogo}
                    />
                  </div>
                  <p className="font-bold text-base leading-6 text-[#f27121] mb-0 whitespace-nowrap">
                    {item.awayName}
                  </p>
                </div>
              </div>

              {/* Score Inputs Column */}
              <div className="flex flex-col gap-3">
                {/* Home Score */}
                <Form.Item className="mb-0" name={`homeScore_${item.matchId}`}>
                  <InputNumber
                    disabled={timeServer > item.countdown}
                    controls={false}
                    min={0}
                    max={99}
                    maxLength={2}
                    placeholder="-"
                    className="bg-[#dcdfe3] border-0 w-full text-center font-bold text-sm text-[#3e4c5c] rounded-[8px]"
                    style={{
                      textAlign: 'center',
                      width: '60px',
                      height: '36px',
                      padding: '8px 16px'
                    }}
                  />
                </Form.Item>

                {/* Away Score */}
                <Form.Item className="mb-0" name={`awayScore_${item.matchId}`}>
                  <InputNumber
                    disabled={timeServer > item.countdown}
                    controls={false}
                    min={0}
                    max={99}
                    maxLength={2}
                    placeholder="-"
                    className="bg-[#dcdfe3] border-0 w-full text-center font-bold text-sm text-[#3e4c5c] rounded-[8px]"
                    style={{
                      textAlign: 'center',
                      width: '60px',
                      height: '36px',
                      padding: '8px 16px'
                    }}
                  />
                </Form.Item>
              </div>
            </div>

            {/* Similar Predictions Info and Confirm Button */}
            <div className="flex gap-3 items-center px-5">
              <div className="flex-1 bg-white border border-[#b8bfc7] rounded-[8px] px-2 h-[40px] flex items-center">
                <Form.Item className="mb-0 w-full" name={`sameScore_${item.matchId}`}>
                  <InputNumber
                    disabled={timeServer > item.countdown}
                    controls={false}
                    min={0}
                    maxLength={5}
                    placeholder="Số người dự đoán giống bạn"
                    className="w-full !border-0 !shadow-none !h-[40px] text-center font-normal text-base text-[#3e4c5c] [&_.ant-input-number-input]:!h-full [&_.ant-input-number-input]:!leading-[44px] [&_.ant-input-number-input]:!p-0"
                  />
                </Form.Item>
              </div>
              <Button
                type="primary"
                htmlType="button"
                loading={isLoadingSubmit}
                onClick={handleMatchCardConfirmClick}
                className="bg-[#f27121] border border-[#fac6a6] h-[40px] text-white font-bold text-sm rounded-[8px] px-4 py-2.5 whitespace-nowrap"
                style={{
                  background: 'linear-gradient(to right, #f27121, #f27121)',
                  border: '1px solid #fac6a6'
                }}
              >
                XÁC NHẬN
              </Button>
            </div>

            {/* League Name - Bottom */}
            <div className="bg-[#f27121] rounded-bl-[20px] rounded-br-[20px] px-4 py-1">
              <p className="font-bold text-base leading-6 text-white text-center mb-0 whitespace-nowrap">
                {item.leagueName}
              </p>
            </div>
          </div>
        );
      });
      if (!itemMatch || itemMatch.length <= 0) {
        const featuredMatches = getMatchSourceList().slice(0, 2);
        return (
          <div className="w-full py-4 px-2 lg:px-3">
            <p className="text-center text-[#4b5563] text-[15px] lg:text-[18px] font-medium mt-2">
              Hiện tại không có trận đấu nào để dự đoán
            </p>
            {renderFeaturedMatches(featuredMatches)}
          </div>
        );
      } else {
        return (
          <div className="w-full h-full grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2 lg:px-3 items-center justify-center">
            {itemMatch}
          </div>
        );
      }
    }
  };

  const filteredChampionTeams = isChampionAutoFilled
    ? championTeams
    : championTeams.filter((t) => t.name.toLowerCase().includes(championQuery.trim().toLowerCase()));

  const selectedChampion = championTeams.find((t) => t.id === selectedChampionId) || null;

  const filteredTopScorerPlayers = isTopScorerAutoFilled
    ? topScorerPlayersList
    : topScorerPlayersList.filter((p) =>
        p.name.toLowerCase().includes(topScorerQuery.trim().toLowerCase())
      );
  const selectedTopScorer =
    topScorerPlayersList.find((p) => String(p.id) === String(selectedTopScorerId)) || null;

  const filteredBestPlayerPlayers = isBestPlayerAutoFilled
    ? bestPlayerPlayersList
    : bestPlayerPlayersList.filter((p) =>
        p.name.toLowerCase().includes(bestPlayerQuery.trim().toLowerCase())
      );
  const selectedBestPlayer =
    bestPlayerPlayersList.find((p) => String(p.id) === String(selectedBestPlayerId)) || null;

  const filteredBestTeamGoalTeams = isBestTeamGoalAutoFilled
    ? championTeams
    : championTeams.filter((t) =>
        t.name.toLowerCase().includes(bestTeamGoalQuery.trim().toLowerCase())
      );
  const selectedBestTeamGoal = championTeams.find((t) => t.id === selectedBestTeamGoalId) || null;

  const filteredFinalAsiaTeams = isFinalAsiaAutoFilled
    ? finalAsiaTeams
    : finalAsiaTeams.filter((t) =>
        t.name.toLowerCase().includes(finalAsiaQuery.trim().toLowerCase())
      );
  const selectedFinalAsiaTeam =
    finalAsiaTeams.find((t) => String(t.id) === String(selectedFinalAsiaTeamId)) || null;

  const isVoteSubmitting =
    isChampionVoting ||
    isTopScorerVoting ||
    isBestPlayerVoting ||
    isFinalAsiaVoting ||
    isBestTeamGoalVoting;

  const closeVoteConfirm = () => {
    if (isVoteSubmitting) return;
    setVoteConfirm({
      open: false,
      type: '',
      title: '',
      selectedName: '',
      selectedImage: '',
      payload: null,
      error: ''
    });
  };

  const handleConfirmVote = async () => {
    if (!voteConfirm?.type || !voteConfirm?.payload) return;

    if (!isClientLoggedIn()) {
      openAppLoginModal();
      return;
    }

    if (voteConfirm.type === 'champion') {
      setIsChampionVoting(true);
      try {
        const res = await voteWorldCupChampion(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;

        if (ok) {
          localStorage.setItem('prediction_champion', voteConfirm.payload.teamId);
          notification.success({
            message: 'Thành công',
            description: `Bạn đã dự đoán: ${voteConfirm.payload.teamName}`
          });

          const championsRes = await getTeamsChampions();
          const rawChampions =
            championsRes?.code === 0 && Array.isArray(championsRes?.data)
              ? championsRes.data
              : unwrapApiList(championsRes);
          const championRows = rawChampions
            .map((row, i) => mapTeamsChampionsRow(row, i))
            .filter(Boolean)
            .sort((a, b) => (b.votes || 0) - (a.votes || 0));
          setChampionLeaderboard(championRows);
          closeVoteConfirm();
        } else {
          setVoteConfirm((prev) => ({
            ...prev,
            error: res?.text_mess || res?.message || 'Không thể bình chọn đội vô địch.'
          }));
        }
      } catch (e) {
        setVoteConfirm((prev) => ({
          ...prev,
          error: e?.message || 'Không thể bình chọn đội vô địch.'
        }));
      } finally {
        setIsChampionVoting(false);
      }
      return;
    }

    if (voteConfirm.type === 'top-scorer') {
      setIsTopScorerVoting(true);
      try {
        const res = await voteWorldCupTopScorer(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;

        if (ok) {
          localStorage.setItem('prediction_top_scorer', voteConfirm.payload.playerId);
          notification.success({
            message: 'Thành công',
            description: `Bạn đã dự đoán: ${voteConfirm.payload.playerName}`
          });

          const topScorerPredRes = await getTopScorerPredictions({
            params: { site: CONST_SITE.SC88 }
          });
          const rawTopScorer =
            topScorerPredRes?.code === 0 && Array.isArray(topScorerPredRes?.data)
              ? topScorerPredRes.data
              : unwrapApiList(topScorerPredRes);
          const topScorerRows = rawTopScorer
            .map((row, i) => {
              const m = mapTopScorerPredictionRow(row, i);
              return m ? { m, i } : null;
            })
            .filter(Boolean)
            .sort((a, b) => {
              const dv = (b.m.votes || 0) - (a.m.votes || 0);
              if (dv !== 0) return dv;
              return a.i - b.i;
            })
            .slice(0, 10)
            .map((x) => x.m);

          setTopScorerLeaderboard(topScorerRows);
          closeVoteConfirm();
        } else {
          setVoteConfirm((prev) => ({
            ...prev,
            error: res?.text_mess || res?.message || 'Không thể bình chọn vua phá lưới.'
          }));
        }
      } catch (e) {
        setVoteConfirm((prev) => ({
          ...prev,
          error: e?.message || 'Không thể bình chọn vua phá lưới.'
        }));
      } finally {
        setIsTopScorerVoting(false);
      }
      return;
    }

    if (voteConfirm.type === 'best-player') {
      setIsBestPlayerVoting(true);
      try {
        const res = await voteWorldCupBestPlayer(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;

        if (ok) {
          localStorage.setItem('prediction_best_player', voteConfirm.payload.playerId);
          notification.success({
            message: 'Thành công',
            description: `Bạn đã dự đoán: ${voteConfirm.payload.playerName}`
          });

          const bestPlayerPredRes = await getBestPlayerPredictions({
            params: { site: CONST_SITE.SC88 }
          });
          const rawBestPlayer =
            bestPlayerPredRes?.code === 0 && Array.isArray(bestPlayerPredRes?.data)
              ? bestPlayerPredRes.data
              : unwrapApiList(bestPlayerPredRes);
          const bestPlayerRows = rawBestPlayer
            .map((row, i) => {
              const m = mapTopScorerPredictionRow(row, i);
              return m ? { m, i } : null;
            })
            .filter(Boolean)
            .sort((a, b) => {
              const dv = (b.m.votes || 0) - (a.m.votes || 0);
              if (dv !== 0) return dv;
              return a.i - b.i;
            })
            .slice(0, 10)
            .map((x) => x.m);

          setBestPlayerLeaderboard(bestPlayerRows);
          closeVoteConfirm();
        } else {
          setVoteConfirm((prev) => ({
            ...prev,
            error: res?.text_mess || res?.message || 'Không thể bình chọn cầu thủ xuất sắc nhất.'
          }));
        }
      } catch (e) {
        setVoteConfirm((prev) => ({
          ...prev,
          error: e?.message || 'Không thể bình chọn cầu thủ xuất sắc nhất.'
        }));
      } finally {
        setIsBestPlayerVoting(false);
      }
      return;
    }

    if (voteConfirm.type === 'final-asia-team') {
      setIsFinalAsiaVoting(true);
      try {
        const res = await voteWorldCupFinalAsiaTeam(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;

        if (ok) {
          localStorage.setItem('prediction_final_asia_team', voteConfirm.payload.teamId);
          notification.success({
            message: 'Thành công',
            description: `Bạn đã dự đoán: ${voteConfirm.payload.teamName}`
          });

          const finalAsiaRes = await getFinalAsiaTeamPredictions({
            params: { site: CONST_SITE.SC88 }
          });
          const rawFinalAsia =
            finalAsiaRes?.code === 0 && Array.isArray(finalAsiaRes?.data)
              ? finalAsiaRes.data
              : unwrapApiList(finalAsiaRes);
          const finalAsiaRows = rawFinalAsia
            .map((row, i) => mapTeamsChampionsRow(row, i))
            .filter(Boolean)
            .sort((a, b) => (b.votes || 0) - (a.votes || 0));
          setFinalAsiaLeaderboard(finalAsiaRows);
          closeVoteConfirm();
        } else {
          setVoteConfirm((prev) => ({
            ...prev,
            error:
              res?.text_mess || res?.message || 'Không thể gửi dự đoán đội Châu Á vào sâu nhất.'
          }));
        }
      } catch (e) {
        setVoteConfirm((prev) => ({
          ...prev,
          error: e?.message || 'Không thể gửi dự đoán đội Châu Á vào sâu nhất.'
        }));
      } finally {
        setIsFinalAsiaVoting(false);
      }
      return;
    }

    if (voteConfirm.type === 'best_team_goal_in_match') {
      setIsBestTeamGoalVoting(true);
      try {
        const res = await voteWorldCupBestTeamGoalInMatch(voteConfirm.payload);
        const ok = res?.code === 0 || res?.code === 200 || res?.success === true;

        if (ok) {
          localStorage.setItem('prediction_best_team_goal_in_match', voteConfirm.payload.teamId);
          notification.success({
            message: 'Thành công',
            description: `Bạn đã dự đoán: ${voteConfirm.payload.teamName}`
          });

          const bestTeamGoalRes = await getBestTeamGoalInMatchPredictions({
            params: { site: CONST_SITE.SC88 }
          });
          const rawBestTeamGoal =
            bestTeamGoalRes?.code === 0 && Array.isArray(bestTeamGoalRes?.data)
              ? bestTeamGoalRes.data
              : unwrapApiList(bestTeamGoalRes);
          const bestTeamGoalRows = rawBestTeamGoal
            .map((row, i) => mapTeamsChampionsRow(row, i))
            .filter(Boolean)
            .sort((a, b) => (b.votes || 0) - (a.votes || 0));
          setBestTeamGoalLeaderboard(bestTeamGoalRows);
          closeVoteConfirm();
        } else {
          setVoteConfirm((prev) => ({
            ...prev,
            error:
              res?.text_mess ||
              res?.message ||
              'Không thể gửi dự đoán đội ghi nhiều bàn nhất trong 1 trận.'
          }));
        }
      } catch (e) {
        setVoteConfirm((prev) => ({
          ...prev,
          error: e?.message || 'Không thể gửi dự đoán đội ghi nhiều bàn nhất trong 1 trận.'
        }));
      } finally {
        setIsBestTeamGoalVoting(false);
      }
    }
  };

  const renderChampionTab = () => {
    const onSubmitChampion = () => {
                if (!isClientLoggedIn()) {
                  openAppLoginModal();
                  return;
                }
                if (!selectedChampion) {
                  notification.warning({
                    message: 'Cảnh báo',
                    description: 'Vui lòng chọn 1 đội để dự đoán.'
                  });
                  return;
                }
                if (!IP || !FP) {
                  notification.warning({
                    message: 'Cảnh báo',
                    description: 'Đang tải thông tin IP/FP, vui lòng thử lại sau.'
                  });
                  return;
                }

                setVoteConfirm({
                  open: true,
                  type: 'champion',
                  title: 'Đội vô địch',
                  selectedName: selectedChampion.name,
                  selectedImage: selectedChampion.flag || '',
                  payload: {
                    ip: IP?.ip ?? IP,
                    fp: FP?.fp ?? FP,
                    site: SITE,
                    teamName: selectedChampion.name,
                    teamId: selectedChampion.id
                  },
                  error: ''
                });
    };

    return (
      <ChampionPredictionTab
        championLeaderboard={championLeaderboard}
        championTeams={championTeams}
        filteredChampionTeams={filteredChampionTeams}
        championQuery={championQuery}
        setChampionQuery={(value) => {
          setIsChampionAutoFilled(false);
          setChampionQuery(value);
        }}
        isChampionVoting={isChampionVoting}
        onSubmitChampion={onSubmitChampion}
        selectedChampionId={selectedChampionId}
        setSelectedChampionId={(id) => {
          setSelectedChampionId(id);
          const team = championTeams.find((t) => String(t.id) === String(id));
          if (team?.name) {
            setChampionQuery(team.name);
            setIsChampionAutoFilled(true);
          }
        }}
        fillChampionQueryFromPick={(name) => {
          setIsChampionAutoFilled(true);
          setChampionQuery(name);
        }}
      />
    );
  };

  const renderTopScorerTab = ({
    panelTitle = 'Dự đoán vua phá lưới',
    confirmTitle = 'Vua phá lưới'
  } = {}) => {
    const onSubmitTopScorer = () => {
      if (!isClientLoggedIn()) {
        openAppLoginModal();
        return;
      }
      if (!selectedTopScorer) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 cầu thủ để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Đang tải thông tin IP/FP, vui lòng thử lại sau.'
        });
        return;
      }

      setVoteConfirm({
        open: true,
        type: 'top-scorer',
        title: confirmTitle,
        selectedName: selectedTopScorer.name,
        selectedImage: selectedTopScorer.photo || '',
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: SITE,
          playerName: selectedTopScorer.name,
          teamName: selectedTopScorer.teamName,
          playerId: selectedTopScorer.id
        },
        error: ''
      });
    };

    return (
      <PlayerPredictionTab
        title={panelTitle}
        leaderboard={topScorerLeaderboard}
        players={filteredTopScorerPlayers}
        catalogPlayers={topScorerPlayersList}
        query={topScorerQuery}
        setQuery={(value) => {
          setIsTopScorerAutoFilled(false);
          setTopScorerQuery(value);
        }}
        selectedId={selectedTopScorerId}
        setSelectedId={(id) => {
          setSelectedTopScorerId(id);
          const player = topScorerPlayersList.find((p) => String(p.id) === String(id));
          if (player?.name) {
            setTopScorerQuery(player.name);
            setIsTopScorerAutoFilled(true);
          }
        }}
        loading={isTopScorerVoting}
        onSubmit={onSubmitTopScorer}
        fillPlayerQueryFromPick={(name) => {
          setIsTopScorerAutoFilled(true);
          setTopScorerQuery(name);
        }}
      />
    );
  };


  const renderBestPlayerTab = () => {
    const onSubmitBestPlayer = () => {
      if (!isClientLoggedIn()) {
        openAppLoginModal();
        return;
      }
      if (!selectedBestPlayer) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 cầu thủ để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Đang tải thông tin IP/FP, vui lòng thử lại sau.'
        });
        return;
      }

      setVoteConfirm({
        open: true,
        type: 'best-player',
        title: 'Cầu thủ xuất sắc nhất',
        selectedName: selectedBestPlayer.name,
        selectedImage: selectedBestPlayer.photo || '',
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: SITE,
          playerName: selectedBestPlayer.name,
          teamName: selectedBestPlayer.teamName,
          playerId: selectedBestPlayer.id
        },
        error: ''
      });
    };

    return (
      <PlayerPredictionTab
        title="Dự đoán cầu thủ xuất sắc nhất"
        leaderboard={bestPlayerLeaderboard}
        players={filteredBestPlayerPlayers}
        catalogPlayers={bestPlayerPlayersList}
        query={bestPlayerQuery}
        setQuery={(value) => {
          setIsBestPlayerAutoFilled(false);
          setBestPlayerQuery(value);
        }}
        selectedId={selectedBestPlayerId}
        setSelectedId={(id) => {
          setSelectedBestPlayerId(id);
          const player = bestPlayerPlayersList.find((p) => String(p.id) === String(id));
          if (player?.name) {
            setBestPlayerQuery(player.name);
            setIsBestPlayerAutoFilled(true);
          }
        }}
        loading={isBestPlayerVoting}
        onSubmit={onSubmitBestPlayer}
        fillPlayerQueryFromPick={(name) => {
          setIsBestPlayerAutoFilled(true);
          setBestPlayerQuery(name);
        }}
      />
    );
  };

  const renderFinalAsiaTeamTab = () => {
    const onSubmitFinalAsia = () => {
      if (!isClientLoggedIn()) {
        openAppLoginModal();
        return;
      }
      if (!selectedFinalAsiaTeam) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 đội để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Đang tải thông tin IP/FP, vui lòng thử lại sau.'
        });
        return;
      }

      setVoteConfirm({
        open: true,
        type: 'final-asia-team',
        title: 'Đội Châu Á vào sâu nhất',
        selectedName: selectedFinalAsiaTeam.name,
        selectedImage: selectedFinalAsiaTeam.flag || '',
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: SITE,
          teamName: selectedFinalAsiaTeam.name,
          teamId: selectedFinalAsiaTeam.id
        },
        error: ''
      });
    };

    return (
      <ChampionPredictionTab
        championLeaderboard={finalAsiaLeaderboard}
        championTeams={finalAsiaTeams}
        filteredChampionTeams={filteredFinalAsiaTeams}
        championQuery={finalAsiaQuery}
        setChampionQuery={(value) => {
          setIsFinalAsiaAutoFilled(false);
          setFinalAsiaQuery(value);
        }}
        isChampionVoting={isFinalAsiaVoting}
        onSubmitChampion={onSubmitFinalAsia}
        selectedChampionId={selectedFinalAsiaTeamId}
        setSelectedChampionId={(id) => {
          setSelectedFinalAsiaTeamId(id);
          const team = finalAsiaTeams.find((t) => String(t.id) === String(id));
          if (team?.name) {
            setFinalAsiaQuery(team.name);
            setIsFinalAsiaAutoFilled(true);
          }
        }}
        votePanelTitle="Đội Châu Á vào sâu nhất"
        searchPlaceholder="Tìm đội Châu Á..."
        fillChampionQueryFromPick={(name) => {
          setIsFinalAsiaAutoFilled(true);
          setFinalAsiaQuery(name);
        }}
      />
    );
  };

  const renderBestTeamGoalInMatchTab = () => {
    const onSubmit = () => {
      if (!isClientLoggedIn()) {
        openAppLoginModal();
        return;
      }
      if (!selectedBestTeamGoal) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng chọn 1 đội để dự đoán.'
        });
        return;
      }
      if (!IP || !FP) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Đang tải thông tin IP/FP, vui lòng thử lại sau.'
        });
        return;
      }

      setVoteConfirm({
        open: true,
        type: 'best_team_goal_in_match',
        title: 'Đội ghi nhiều bàn nhất trong 1 trận',
        selectedName: selectedBestTeamGoal.name,
        selectedImage: selectedBestTeamGoal.flag || '',
        payload: {
          ip: IP?.ip ?? IP,
          fp: FP?.fp ?? FP,
          site: SITE,
          teamName: selectedBestTeamGoal.name,
          teamId: selectedBestTeamGoal.id
        },
        error: ''
      });
    };

    return (
      <ChampionPredictionTab
        championLeaderboard={bestTeamGoalLeaderboard}
        championTeams={championTeams}
        filteredChampionTeams={filteredBestTeamGoalTeams}
        championQuery={bestTeamGoalQuery}
        setChampionQuery={(value) => {
          setIsBestTeamGoalAutoFilled(false);
          setBestTeamGoalQuery(value);
        }}
        isChampionVoting={isBestTeamGoalVoting}
        onSubmitChampion={onSubmit}
        selectedChampionId={selectedBestTeamGoalId}
        setSelectedChampionId={(id) => {
          setSelectedBestTeamGoalId(id);
          const team = championTeams.find((t) => String(t.id) === String(id));
          if (team?.name) {
            setBestTeamGoalQuery(team.name);
            setIsBestTeamGoalAutoFilled(true);
          }
        }}
        votePanelTitle="Đội ghi nhiều bàn nhất trong 1 trận"
        searchPlaceholder="Tìm đội của bạn..."
        fillChampionQueryFromPick={(name) => {
          setIsBestTeamGoalAutoFilled(true);
          setBestTeamGoalQuery(name);
        }}
      />
    );
  };

  const renderOtherTabPlaceholder = (label) => {
    return (
      <div className="w-full py-12 flex flex-col items-center justify-center">
        <InboxOutlined className="text-5xl text-gray-300 mb-3" />
        <div className="text-sm lg:text-base text-[#6b7280]">
          Nội dung tab <span className="font-extrabold text-[#111827]">{label}</span> đang được cập
          nhật.
        </div>
      </div>
    );
  };

  const renderActiveContentInFrame = () => {
    const activeLabel = predictionTabs.find((t) => t.key === activePredictionTab)?.label;

    if (activePredictionTab === 'match') return null;

    return (
      <div className="w-full px-1 lg:px-3 lg:pt-4 pb-2">
        <div className="rounded-2xl border-2 border-[#ef8a45] bg-white/80 ">
          {activePredictionTab === 'champion'
            ? renderChampionTab()
            : activePredictionTab === 'top-scorer'
              ? renderTopScorerTab()
              : activePredictionTab === 'best-player'
                ? renderBestPlayerTab()
                : activePredictionTab === 'final-asia-team'
                  ? renderFinalAsiaTeamTab()
                  : activePredictionTab === 'best_team_goal_in_match'
                    ? renderBestTeamGoalInMatchTab()
                    : renderOtherTabPlaceholder(activeLabel)}
        </div>
      </div>
    );
  };

  return (
    <div className={twMerge(SITE, 'w-full h-full overflow-x-hidden')}>
      <img
        src="bg.png"
        alt="Background"
        className="w-full h-full fixed right-0 bottom-0 object-cover object-center"
      />
      <div className="container relative z-10 pb-[24px] lg:pb-[32px] lg:px-2">
        <Header />

        <div className="mx-auto mt-2 max-w-[1200px] px-2">
          <div className="w-full overflow-x-auto scrollbar-custom pb-1">
            <div className="flex w-max min-w-full items-center gap-2 py-2 px-1 lg:justify-between">
              {predictionTabs.map((tab) => {
                const isActive = activePredictionTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActivePredictionTab(tab.key)}
                    className={twMerge(
                      'shrink-0 p-0 m-0 bg-transparent border-0 outline-none focus:outline-none focus:ring-0 transition-transform duration-200 hover:scale-[1.02] active:scale-[0.99]',
                      tab.key === 'match'
                        ? 'w-[112px] h-[37px] lg:w-[182px] lg:h-[60px]'
                        : 'w-[118px] h-[42px] lg:w-[188px] lg:h-[66px]'
                    )}
                  >
                    <img
                      src={isActive ? tab.iconActive : tab.icon}
                      alt={tab.label}
                      className="w-full h-full object-contain select-none pointer-events-none block"
                      draggable={false}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div
          className="mx-auto mt-2 max-w-[1200px] w-full p-2 lg:pb-8 lg:pt-4 rounded-3xl border border-solid "
          style={{
            background: 'white',
            borderColor: '#FAC6A6',
            borderWidth: '1.25px',
            borderRadius: '1.5rem',
            boxShadow: '0 0 0 4px rgba(255, 202, 141, 0.4)'
          }}
        >
          {showMatchPredictionTab && activePredictionTab === 'match' ? (
            <>
              <div className="relative w-full h-full">
                <Form
                  name="formMatch"
                  onFinish={onFinishForm}
                  onFinishFailed={onFinishFailed}
                  autoComplete="off"
                  form={form}
                  initialValues={{ account: getAccountWhenLoggedIn() }}
                >
                  <div className="w-full h-full lg:p-2 lg:px-4">
                    {/* Hàng 1 — mẫu: input + Xác nhận (gộp) | Xem live */}
                    <div className="px-3 lg:px-4 pt-4">
                      <div className="hidden flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                        <div className="flex w-full sm:flex-1 sm:min-w-0 sm:max-w-[min(100%,400px)] items-center gap-2 sm:gap-3">
                          <Form.Item
                            name="account"
                            className="mb-0 min-w-0 flex-1"
                            rules={[
                              {
                                required: true,
                                message: 'Vui lòng điền tên đăng nhập!'
                              }
                            ]}
                          >
                            <Input
                              placeholder="Nhập tên đăng nhập ..."
                              maxLength={20}
                              className="h-[34px] lg:h-[36px] w-full rounded-full border border-[#e5e7eb] bg-white shadow-[0_2px_10px_rgba(15,23,42,0.06)] text-[13px] text-[#374151] placeholder:text-[#9ca3af] px-4"
                            />
                          </Form.Item>
                          <Form.Item className="mb-0 shrink-0">
                            <Button
                              type="primary"
                              htmlType="submit"
                              loading={isLoadingSubmit}
                              className="h-[34px] sm:h-[36px] min-w-[92px] sm:min-w-[108px] rounded-full border-0 px-4 sm:px-5 font-bold text-[13px] sm:text-[14px] text-white shadow-[0_3px_10px_rgba(242,113,33,0.35)]"
                              style={{
                                background: 'linear-gradient(180deg, #fb9a4a 0%, #e86818 100%)'
                              }}
                            >
                              Xác nhận
                            </Button>
                          </Form.Item>
                        </div>
                        <Button
                          type="button"
                          className="hidden lg:block h-[34px] sm:h-[36px] w-full sm:w-auto shrink-0 rounded-full px-6 sm:px-7 font-bold text-[13px] sm:text-[14px] text-white border-0 shadow-[0_4px_14px_rgba(160,70,30,0.35)]"
                          style={{
                            background: '#F27121'
                          }}
                          onClick={() => {
                            notification.info({
                              message: 'Xem live',
                              description: 'Link phát sóng sẽ được cập nhật sau khi tích hợp API.'
                            });
                          }}
                        >
                          Xem live
                        </Button>
                      </div>
                    </div>

                    {/* Hàng 2 — carousel giải (dàn trải đều trong vùng giữa) */}
                    <div className="flex gap-2 sm:gap-3 mt-0 items-center px-2 sm:px-3">
                      {/* <Button
              type="link"
              icon={<LeftOutlined />}
              onClick={scrollLeft}
                        className="text-black !px-0 !mx-0 shrink-0"
            /> */}
                      <div
                        className="flex w-full max-w-[900px] mx-auto min-w-0 flex-1 justify-between  items-center gap-1 overflow-x-auto scrollbar-custom pb-2 py-0.5"
                        ref={leagueListRef}
                        style={{ scrollBehavior: 'smooth' }}
                      >
                        {leagues.map((league) => (
                          <div
                            key={league.id}
                            className={twMerge(
                              'flex shrink-0 items-center justify-center rounded-[12px] cursor-pointer transition-all duration-200',
                              selectedLeagues.includes(league.id)
                                ? 'p-1 bg-gradient-to-br from-[#f27121] to-[#63c8f2] shadow-lg shadow-[#63c8f2]/40 border-2 border-[#f27121] hover:scale-105'
                                : 'p-1.5 bg-[#edeff1] border border-solid border-[#dcdfe3] shadow-[0_1px_2px_rgba(15,23,42,0.06)] hover:border-[#b8bfc7] hover:shadow-[0_2px_8px_rgba(15,23,42,0.08)] active:scale-[0.98]'
                            )}
                            style={{ width: '76px', minWidth: '76px', minHeight: '52px' }}
                            onClick={() => handleLeagueClick(league.id)}
                          >
                            <img
                              src={league.logo}
                              alt={league.name}
                              className={twMerge(
                                'object-contain',
                                selectedLeagues.includes(league.id)
                                  ? 'h-11 sm:h-12'
                                  : 'h-10 sm:h-11 opacity-95'
                              )}
                            />
                          </div>
                        ))}
                      </div>
                      {/* <Button
              type="link"
              icon={<RightOutlined />}
              onClick={scrollRight}
                        className="text-black !px-0 !mx-0 shrink-0"
            /> */}
                    </div>

                    {renderLeagueFrameDetail()}

                    {/* Hàng 3 — lọc thời gian */}
                    {/* <div className="w-full px-3 lg:px-4 mt-3">
                      <div className="flex items-center justify-start w-full max-w-[280px]">
                          <Select
                            value={activeTab}
                            onChange={(value) => setActiveTab(value)}
                            options={getDateOptions()}
                          className="custom-select w-full h-[34px] sm:h-[36px] rounded-full"
                            popupClassName="w-[300px] custom-select-dropdown"
                          />
                        </div>
                    </div> */}

                    {isLoading ? (
                      <div className="w-full h-full text-center py-8">
                        <Spin />
                      </div>
                    ) : (
                      <div className="w-full h-full">{showListItemMatch(getFilteredMatches())}</div>
                    )}
                  </div>
                </Form>
              </div>
            </>
          ) : (
            renderActiveContentInFrame()
          )}
        </div>
        {/* Lịch Thi Đấu */}
        {/* <div
          className="hidden mx-auto max-w-[1320px] bg-white/95 border-2 border-solid shadow shadow-primary mt-4 p-2 w-full lg:pb-4 lg:pt-4 rounded-3xl"
          style={{
            background: '#FFFFFF80',
            borderColor: '#f27121',
            borderRadius: '1.5rem'
          }}
        >
          <div className="flex items-center justify-center p-3">
            <img
              src="/assets/images/match/plan.png"
              alt="Lịch thi đấu hot"
              className="h-auto max-h-[60px] object-contain"
            />
          </div>

          <div className="p-3 border-none border-0">
            <iframe
              src="https://football-widget.pages.dev/"
              //   width="100%"
              height="600"
              //   frameBorder="0"
              //   allowFullScreen
              title="Football Widget"
              className="w-full border-0 border-none rounded-lg"
            />
          </div>
        </div> */}

        {isPredictConfirmOpen ? (
          <div className="fixed inset-0 z-[60] flex items-center justify-center px-3">
            <div
              className="absolute inset-0 bg-black/20"
              aria-hidden="true"
              onClick={isLoadingSubmit ? undefined : onCancelMatchConfirm}
            />

            <div
              role="dialog"
              aria-modal="true"
              aria-label="Xác nhận dự đoán trận đấu"
              className="relative w-full max-w-[520px] bg-[#fff2eb] border-[2.289px] border-solid border-white rounded-[10px] p-[18px] md:p-[30px] backdrop-blur-[24.266px]"
            >
              <div className="flex justify-center mb-3">
                <div className="w-[68px] h-[68px] md:w-[80px] md:h-[80px] bg-[#f27121] rounded-full flex items-center justify-center shadow-lg">
                  <svg
                    className="w-[40px] h-[40px] md:w-[50px] md:h-[50px] text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>

              <div className="text-center font-extrabold text-[#f27121] text-[20px] md:text-[24px] leading-[32px] md:leading-[40px]">
                XÁC NHẬN DỰ ĐOÁN
              </div>

              <div className="mt-2 text-center text-[#2a2a2a] font-medium text-[14px] md:text-[16px] leading-[22px] md:leading-[26px]">
                <div className="mb-2">Bạn đã chọn dự đoán các trận đấu sau:</div>

                <div className="max-h-[300px] overflow-y-auto space-y-2 px-2">
                  {pendingMatchPredictions.map((match, index) => (
                    <div key={match.matchId || index} className="bg-white/60 rounded-lg p-2 md:p-3">
                      <div className="font-bold text-[#333]">
                        {match.homeName} - {match.awayName}
                      </div>
                      <div className="text-[#f27121] font-bold text-[16px] md:text-[18px]">
                        Tỷ số: {match.homeName}{' '}
                        <span className="text-[#e74c3c]">{match.homeScore}</span> -{' '}
                        <span className="text-[#e74c3c]">{match.awayScore}</span> {match.awayName}
                      </div>
                      <div className="text-[#666] text-[12px] md:text-[14px]">
                        Số người dự đoán giống bạn:{' '}
                        <span className="font-bold text-[#f27121]">{match.sameScore}</span>
                      </div>
                      {match.firstGoal ? (
                        <div className="text-[#666] text-[12px] md:text-[14px]">
                          Đội ghi bàn đầu tiên:{' '}
                          <span className="font-bold text-[#f27121]">
                            {match.firstGoal === 'home' ? match.homeName : match.awayName}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>

                <div className="mt-3 italic text-[13px] md:text-[14px]">
                  Hãy cùng chờ đợi kết quả nhé.
                </div>
              </div>

              <div className="mt-4 flex items-center justify-center gap-[18px]">
                <button
                  type="button"
                  disabled={isLoadingSubmit}
                  onClick={onConfirmMatchPrediction}
                  className={twMerge(
                    'w-[143.5px] h-[38px] md:h-auto border-none bg-[#f27121] text-white rounded-[9.965px] px-[9.965px] py-[9.965px] font-semibold text-[13.951px]',
                    isLoadingSubmit ? 'opacity-60 cursor-not-allowed' : ''
                  )}
                >
                  {isLoadingSubmit ? 'ĐANG GỬI...' : 'XÁC NHẬN'}
                </button>

                <button
                  type="button"
                  disabled={isLoadingSubmit}
                  onClick={onCancelMatchConfirm}
                  className={twMerge(
                    'w-[143.5px] h-[38px] md:h-auto border-none bg-[#a2b1b8] text-white rounded-[9.965px] px-[9.965px] py-[9.965px] font-semibold text-[13.951px]',
                    isLoadingSubmit ? 'opacity-60 cursor-not-allowed' : ''
                  )}
                >
                  HỦY BỎ
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {voteConfirm.open ? (
          <div className="fixed inset-0 z-[1050] flex items-center justify-center px-4 py-6">
            <div
              className="absolute inset-0 bg-black/45"
              aria-hidden
              onClick={isVoteSubmitting ? undefined : closeVoteConfirm}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="vote-confirm-title"
              className="relative z-[1] w-full max-w-[360px] overflow-hidden rounded-2xl border border-white/90 shadow-2xl lg:max-w-[480px] lg:rounded-3xl xl:max-w-[520px]"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id="vote-confirm-title" className="sr-only">
                Xác nhận bình chọn {voteConfirm.title}
              </h2>
              <div className="relative">
                <button
                  type="button"
                  onClick={closeVoteConfirm}
                  disabled={isVoteSubmitting}
                  className="absolute right-2 top-2 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-[#1f1f1f] bg-gradient-to-b from-[#ff6b6b] via-[#e53935] to-[#b71c1c] text-white shadow-[inset_0_2px_5px_rgba(255,255,255,0.55),inset_0_-6px_14px_rgba(0,0,0,0.28),0_0_0_2px_#eceff1,0_0_0_3px_#78909c,0_4px_12px_rgba(0,0,0,0.45)] transition-[filter,transform] hover:brightness-[1.06] active:scale-[0.96] active:brightness-[0.96] disabled:pointer-events-none disabled:opacity-50 sm:right-3 sm:top-3 sm:h-10 sm:w-10 lg:right-4 lg:top-4 lg:h-8 lg:w-8"
                  aria-label="Đóng"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className="h-[46%] w-[46%] sm:h-[44%] sm:w-[44%]"
                    aria-hidden
                  >
                    <path
                      d="M7 7l10 10M17 7L7 17"
                      stroke="currentColor"
                      strokeWidth="2.35"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>

                <div
                  className="relative w-full overflow-hidden bg-[#c5e3f7] bg-cover bg-center bg-no-repeat aspect-[888/707]"
                  style={{ backgroundImage: 'url(/assets/images/tab/popup.png)' }}
                >
                  <div className="pointer-events-none absolute max-[400px]:left-1/2 max-[400px]:top-[3%] max-[400px]:w-[40%] left-1/2 top-[5%] z-[5] w-[min(58%,200px)] max-w-[220px] -translate-x-1/2 sm:top-[6%] sm:max-w-[130px] lg:top-[5.5%] lg:max-w-[276px] xl:max-w-[300px] aspect-[372/450]">
                    <div className="absolute max-[400px]:left-1/2 max-[400px]:top-[45%] max-[400px]:w-[65%] left-1/2 top-[31%] z-0 aspect-square w-[41%] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full bg-white shadow-[inset_0_1px_6px_rgba(0,0,0,0.12)] sm:top-[46%] sm:w-[60%] lg:top-[47%] lg:w-[62%]">
                      {voteConfirm.selectedImage ? (
                        <img
                          src={voteConfirm.selectedImage}
                          alt=""
                          className={twMerge(
                            'block h-full w-full object-cover object-center',
                            voteConfirm.type === 'champion' ||
                              voteConfirm.type === 'final-asia-team' ||
                              voteConfirm.type === 'best_team_goal_in_match'
                              ? 'origin-center scale-[1.28] sm:scale-[1.22] lg:scale-[1.18]'
                              : ''
                          )}
                          draggable={false}
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-[#e8eef4] text-[11px] font-medium text-[#94a3b8]">
                          —
                        </div>
                      )}
                    </div>
                    <img
                      src="/assets/images/tab/khung.png"
                      alt=""
                      className="pointer-events-none absolute inset-0 z-10 h-full w-full select-none object-contain object-top"
                      draggable={false}
                    />
                  </div>

                  <div className="pointer-events-auto absolute inset-x-0 bottom-0 z-[15] flex flex-col gap-2 bg-gradient-to-t from-[#b8d9f0] from-[25%] via-[#d0e5f7]/[0.96] to-transparent px-3 pb-3 pt-8 sm:px-4 sm:pb-4 sm:pt-10 lg:gap-3 lg:px-5 lg:pb-5 lg:pt-12">
                    <p className="text-center text-[12px] leading-snug text-[#2a2a2a] sm:text-[14px] sm:leading-relaxed lg:text-[15px] lg:leading-relaxed xl:text-base">
                      Bạn đã bình chọn{' '}
                      <span className="font-bold text-[#2a2a2a]">{voteConfirm.selectedName}</span>{' '}
                      là{' '}
                      <span className="font-semibold text-[#2a2a2a]">
                        {voteConfirm.type === 'champion'
                          ? 'đội vô địch'
                          : voteConfirm.type === 'top-scorer'
                            ? 'Vua Phá Lưới'
                            : voteConfirm.type === 'best-player'
                              ? 'Cầu thủ xuất sắc nhất'
                              : voteConfirm.type === 'final-asia-team'
                                ? 'đội Châu Á vào sâu nhất'
                                : voteConfirm.type === 'best_team_goal_in_match'
                                  ? 'đội ghi nhiều bàn nhất trong 1 trận'
                                  : voteConfirm.title}
                      </span>{' '}
                      World Cup 2026. Bạn có chắc chắn không?
                    </p>

                    {voteConfirm.error ? (
                      <div className="rounded-lg border border-[#f1b2b2] bg-[#fff1f0] px-2 py-1.5 text-center text-[11px] text-[#5f2120] sm:text-xs lg:text-sm lg:py-2">
                        {voteConfirm.error}
                      </div>
                    ) : null}

                    <div className="flex flex-row flex-wrap items-center justify-center gap-2 sm:gap-3 lg:gap-4">
                      <Button
                        type="default"
                        onClick={closeVoteConfirm}
                        disabled={isVoteSubmitting}
                        className="rounded-full px-4 h-9 font-semibold text-[#374151]"
                      >
                        Hủy
                      </Button>
                      <Button
                        type="primary"
                        onClick={handleConfirmVote}
                        loading={isVoteSubmitting}
                        disabled={isVoteSubmitting}
                        className="rounded-full px-5 h-9 font-extrabold text-sm border-0 shadow-md min-w-[100px]"
                        style={{
                          background: 'linear-gradient(180deg, #f08b2f 0%, #df6b1d 100%)',
                          boxShadow: '0 4px 14px rgba(242, 113, 33, 0.35)'
                        }}
                      >
                        Xác nhận
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default HomePage;
